import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import { getWalletType } from '../../constants/walletTypes';

const getWalletsPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.wallets)) return data.wallets;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatDate = (value) => {
  const raw = value?.raw || value?.Raw || value;
  if (!raw) return '-';

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return value?.formatted || value?.Formatted || '-';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const WalletList = () => {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deletingUuid, setDeletingUuid] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);

  const fetchWallets = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/wallets/${user.id}`);
      setWallets(getWalletsPayload(response.data));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Nao foi possivel carregar suas wallets.'
      );
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  const confirmDelete = async () => {
    const walletUuid = confirmTarget?.uuid;
    if (!walletUuid) return;

    try {
      setDeletingUuid(walletUuid);
      setError('');
      await api.delete(`/wallets/${walletUuid}`);
      setConfirmTarget(null);
      await fetchWallets();
      showToast('Wallet removida com sucesso.');
    } catch (err) {
      setConfirmTarget(null);
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Nao foi possivel remover a wallet.';
      setError(message);
      showToast(message, 'error');
    } finally {
      setDeletingUuid('');
    }
  };

  const rows = useMemo(() => {
    return wallets.map((wallet, index) => {
      const type = getWalletType(wallet.type || wallet.Type || wallet.network || wallet.Network);
      const uuid = wallet.uuid || wallet.Uuid || wallet.id || '';
      const id = uuid || `${user?.id}-${index}`;

      return {
        id,
        uuid,
        label: wallet.label || wallet.name || wallet.Name || type.label,
        type,
        address:
          wallet.address ||
          wallet.Address ||
          wallet.wallet ||
          wallet.Wallet ||
          wallet.destination ||
          wallet.Destination ||
          '-',
        createdAt: formatDate(wallet.createdAt || wallet.CreatedAt || wallet.created_at),
      };
    });
  }, [wallets, user?.id]);

  const hasWallets = rows.length > 0;
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="wallet" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Wallet</h1>
              <p className="text-sm text-slate-500">Wallets salvas para uso futuro em saques.</p>
            </div>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Minhas wallets</h2>
                <p className="text-sm text-slate-500">Gerencie destinos Bitcoin, LN e Liquid.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/wallets/add')}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
              >
                <i className="bi-plus-circle"></i> Adicionar Wallet
              </button>
            </div>

            {loading && <div className="text-sm text-gray-500">Carregando wallets...</div>}

            {error && !loading ? (
              <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
            ) : null}

            {!loading && !error && !hasWallets ? (
              <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-3xl text-slate-500">
                  <i className="bi-wallet2"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-800">Nenhuma wallet cadastrada</h3>
                  <p className="text-sm text-slate-500">Adicione uma wallet para agilizar seus proximos saques.</p>
                </div>
              </div>
            ) : null}

            {hasWallets && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm">
                  <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="py-3 pr-4 text-left font-semibold">Nome</th>
                      <th className="py-3 pr-4 text-left font-semibold">Tipo</th>
                      <th className="py-3 pr-4 text-left font-semibold">Wallet</th>
                      <th className="py-3 pr-4 text-left font-semibold">Criada em</th>
                      <th className="py-3 text-right font-semibold">Acoes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-600">
                    {rows.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3 pr-4 font-semibold text-slate-800">{row.label}</td>
                        <td className="py-3 pr-4">
                          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700">
                            <i className={row.type.icon}></i> {row.type.shortLabel}
                          </span>
                        </td>
                        <td className="max-w-xs truncate py-3 pr-4 font-mono text-[11px]" title={row.address}>
                          {row.address}
                        </td>
                        <td className="py-3 pr-4">{row.createdAt}</td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/wallets/edit/${row.uuid}`)}
                              disabled={!row.uuid}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50"
                              title="Editar"
                            >
                              <i className="bi-pencil"></i> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmTarget({ uuid: row.uuid, label: row.label })}
                              disabled={!row.uuid || deletingUuid === row.uuid}
                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 text-[11px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                              title="Remover"
                            >
                              <i className="bi-trash"></i>
                              {deletingUuid === row.uuid ? 'Removendo...' : 'Remover'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>

      {confirmTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => (deletingUuid ? null : setConfirmTarget(null))}
            aria-label="Fechar"
          ></button>
          <div className="relative z-[91] w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-2xl text-red-600">
              <i className="bi-trash"></i>
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Remover wallet</h3>
            <p className="mt-2 text-sm text-slate-500">
              Tem certeza que deseja remover
              {confirmTarget.label ? ` "${confirmTarget.label}"` : ' esta wallet'}? Essa acao nao pode
              ser desfeita.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmTarget(null)}
                disabled={Boolean(deletingUuid)}
                className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(deletingUuid)}
                className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {deletingUuid ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletList;
